import { FC, RefObject, ReactNode, useCallback, useContext, useMemo, useImperativeHandle } from 'react';
import { Controller, FormProvider, FormState, useForm, UseFormReturn, UseFormReset } from 'react-hook-form';
import { Box, Button, FormControlLabel, Grid, Stack, TextField, Typography, FormControl, FormHelperText } from '@mui/material';
import { Add } from '@mui/icons-material';
import { format } from 'date-fns';
import { DialogContext } from '@cvt/contexts/DialogContext/DialogContext';
import omit from 'lodash/omit';

import { DateInputFormat, getPossibleDate } from '@shared/helpers/date';
import { DateField } from '@modules/Tributes/components/Field/DateField';
import { datePickerRange } from '@modules/Tributes/components/PetDetailsManualForm';
import { ShareButtons } from '@modules/Tributes/components/ShareButtons';
import { useDictionary } from '@cvt/hooks/useDictionary';
import { useFormPersist, type Props as PersistProps } from '@cvt/hooks/form/useFormPersist';
import { validateBirthdayDate, validatePassingDate, validatePetName } from '@modules/Tributes/helpers/validators';
import { PetTypeSelect } from '@modules/Tributes/components/PetTypeSelect';
import { PetSexSelect } from '@modules/Tributes/components/PetSexSelect';
import { TimelineEventFieldArrayForm } from '../partials/TimelineEventFieldArrayForm';
import { useSortedFieldArray } from '../hooks/useSortedFieldArray';

import { PictureUploader } from './PictureUploader';
import HeartPulse from './HeartPulse';


type FormData = Tributes.FormData;

export type RefHandle = {
  resetForm: UseFormReset<Tributes.FormData>,
}

interface Props {
    ref?: RefObject<RefHandle | null>;
    onSubmitRequest?: (values: FormData) => void;
    onFieldChange?: (fieldName: keyof FormData, value: any) => void;
    slots?: {
      footer?: ReactNode | ((formState: FormState<FormData>, methods: UseFormReturn<FormData, any, FormData>) => ReactNode);
    };
    readonly?: boolean;
    defaultValues?: Partial<FormData>;
    tributeStatus?: string;
    tributeUrl?: string;
    isEditing?: boolean;
    isCreating?: boolean;
}

const DEFAULT_VALUES: Partial<FormData> = {
  pet: {
    petType: '',
    sex: '',
    name: '',
    birthday: null,
    passingDate: null,
    favouriteMemory: '',
    personality: '',
    specialHabits: '',
  },
  mainPhoto: null,
  timeline: [],
};

const persistProps: Partial<PersistProps<FormData>> = {
  transformInput: ({ pet, timeline }: FormData) => ({
    pet,
    timeline: timeline.map(({ draft, saved }) => ({
      draft: omit(draft, ['media']),
      saved: omit(saved, ['media']),
    })),
  }),
  transformOutput: ({ pet, timeline }: FormData): Partial<FormData> => ({
    pet: {
      ...pet,
      birthday: getPossibleDate(pet.birthday),
      passingDate: getPossibleDate(pet.passingDate),
    },
    timeline: timeline.map((value) => ({
      ...value,
      draft: { ...value.draft, date: getPossibleDate(value.draft.date), media: [] },
      saved: { ...value.saved, date: getPossibleDate(value.saved.date), media: [] },
    })),
  }),
};

export const TimelineForm: FC<Props> = (props) => {
  const { onSubmitRequest, slots, defaultValues = DEFAULT_VALUES, readonly, tributeStatus, tributeUrl, isEditing, isCreating } = props;
  const dictionary = useDictionary();
  const { asyncConfirmation } = useContext(DialogContext);

  const methods = useForm<FormData>({
    mode: 'onBlur',
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    },
  });

  const { control, handleSubmit, formState, watch, reset } = methods;

  useImperativeHandle(props.ref, () => ({
    resetForm: reset,
  }), [reset]);

  const {
    fields,
    append,
    remove,
  } = useSortedFieldArray({
    control,
    name: 'timeline',
    keyName: 'fieldId',
    sortFn: (a, b) => {
      const dateA = a.saved.date ? new Date(a.saved.date) : null;
      const dateB = b.saved.date ? new Date(b.saved.date) : null;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateA.getTime() - dateB.getTime();
    },
  });

  useFormPersist<FormData>('tribute-story', {
    isEnabled: Boolean(isCreating && !readonly),
    isDirty: formState.isDirty,
    isSubmitSuccessful: formState.isSubmitSuccessful,
    setValue: methods.setValue,
    watch,
    ...persistProps,
  });

  const pet = watch('pet');

  const onSubmit = useCallback(async (data: FormData) => {
    const payload = {
      ...data,
    };

    return onSubmitRequest?.(payload);
  }, [onSubmitRequest]);

  const petLifespan = useMemo(() => {
    return `${pet.birthday ? format(pet.birthday, DateInputFormat) : ''} - ${pet.passingDate ? format(pet.passingDate, DateInputFormat) : ''}`;
  }, [pet.birthday, pet.passingDate]);

  const onAddEvent = useCallback(() => {
    const newEvent = {
      media: [],
      date: new Date(),
      text: '',
    };

    append({
      saved: { ...newEvent },
      draft: { ...newEvent },
    });
  }, [append]);

  const onRemoveEvent = useCallback(async (index: number) => {
    // maybe some cleanup logic here
    // like removing files from storage if they were uploaded
    // or removing any related data
    // for now just remove the event
    const userConfirmed = await asyncConfirmation({});

    if (!userConfirmed) {
      return;
    }
    remove(index);
  }, [asyncConfirmation, remove]);

  return (
    <FormProvider {...methods}>
      <form id="tribute-form" style={{ width: '100%', height: '100%' }} onSubmit={handleSubmit(onSubmit)}>
        <Stack
          sx={{
            justifyContent: 'center',
            mt: 3,
            borderRadius: { xs: undefined, sm: '12px' },
          }}
        >
          <Stack justifyContent="space-between" height="100%" gap={4}>
            <Grid container spacing={2} className="MY_TEST_CLASSNAME" position="relative" py={2}>
              <Background />
              <Grid size={12} zIndex={1}>
                <Stack direction="row" alignItems="center" justifyContent="center" className="SOME_TEST">
                  <Controller
                    rules={{
                      required: dictionary.forms.validations.required,
                    }}
                    name="mainPhoto"
                    render={({ field, fieldState }) => (
                      <FormControl>
                        <PictureUploader
                          {...field}
                          disabled={readonly}
                          label="Profile Picture"
                        />
                        {!!fieldState.error && (
                          <FormHelperText sx={{ textAlign: 'center' }} error>
                            {fieldState.error.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Stack>
              </Grid>
              <Grid size={12} zIndex={1}>
                <Stack alignItems="center" justifyContent="center" mt={1} gap={1} px={3}>
                  {(isEditing || isCreating) ? (
                    <FormControlLabel
                      labelPlacement="top"
                      sx={{
                        alignItems: 'flex-start',
                        color: '#263238',
                        m: 0,
                        width: '100%',
                        '.MuiFormControlLabel-label': {
                          fontSize: 14,
                          fontWeight: 500,
                          mb: 1,
                        },
                      }}
                      slotProps={{
                        typography: {
                          fontSize: 12,
                          fontWeight: 400,
                        },
                      }}
                      control={
                        <Controller
                          name="pet.name"
                          rules={{
                            required: dictionary.forms.validations.required,
                            validate: (value) => validatePetName(value, dictionary),
                          }}
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              variant="outlined"
                              color="secondary"
                              placeholder="Enter your pet's name"
                              fullWidth
                              size="small"
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              slotProps={{
                                input: {
                                  sx: {
                                    'fieldset': {
                                      borderColor: '#e2e8f0',
                                      borderRadius: '10px',
                                    },
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      }
                      label="Pet Name *"
                    />
                  ) : (
                    <Typography variant="h1" align="center" fontSize={30} fontWeight={700}>
                      {pet.name || 'Your Pet'}
                    </Typography>
                  )}
                  {(isEditing || isCreating) ? (
                    <FormControlLabel
                      labelPlacement="top"
                      sx={{
                        alignItems: 'flex-start',
                        color: '#263238',
                        m: 0,
                        width: '100%',
                        '.MuiFormControlLabel-label': {
                          fontSize: 14,
                          fontWeight: 500,
                          mb: 1,
                        },
                      }}
                      slotProps={{
                        typography: {
                          fontSize: 12,
                          fontWeight: 400,
                        },
                      }}
                      control={
                        <Controller
                          name="pet.petType"
                          rules={{ required: dictionary.forms.validations.required }}
                          render={({ field, fieldState }) => (
                            <PetTypeSelect
                              {...field}
                              value={field.value || ''}
                              fullWidth
                              size="small"
                              label=""
                              sx={{
                                'fieldset': {
                                  borderColor: '#e2e8f0',
                                  borderRadius: '10px',
                                },
                              }}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          )}
                        />
                      }
                      label="Beloved Companion Type *"
                    />
                  ) : (
                    <Typography variant="body1" align="center" color="textSecondary" fontSize={18} fontWeight={400} sx={{ textTransform: 'capitalize' }}>
                      {pet.petType}
                    </Typography>
                  )}
                  {(isEditing || isCreating) ? (
                    <FormControlLabel
                      required
                      labelPlacement="top"
                      sx={{
                        alignItems: 'flex-start',
                        color: '#263238',
                        m: 0,
                        width: '100%',
                        '.MuiFormControlLabel-label': {
                          fontSize: 14,
                          fontWeight: 500,
                          mb: 1,
                        },
                      }}
                      slotProps={{
                        typography: {
                          fontSize: 12,
                          fontWeight: 400,
                        },
                      }}
                      control={
                        <Controller
                          name="pet.sex"
                          rules={{ required: dictionary.forms.validations.required }}
                          render={({ field, fieldState }) => (
                            <PetSexSelect
                              {...field}
                              value={field.value || ''}
                              fullWidth
                              size="small"
                              label=""
                              sx={{
                                fieldset: {
                                  borderColor: '#e2e8f0',
                                  borderRadius: '10px',
                                },
                              }}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          )}
                        />
                      }
                      label="Pet Gender"
                    />
                  ) : (
                    <Typography variant="body1" align="center" color="textSecondary" fontSize={18} fontWeight={400} sx={{ textTransform: 'capitalize' }}>
                      {pet.sex}
                    </Typography>
                  )}

                  {(isEditing || isCreating) ? (
                    <Grid container size={12} spacing={2}>
                      <Grid size={6}>
                        <DateField<FormData>
                          name="pet.birthday"
                          label={dictionary.forms.tributes.adoptionDate}
                          maxDate={datePickerRange.maxDate}
                          minDate={datePickerRange.minDate}
                          rules={{
                            validate: (value) => {
                              return validateBirthdayDate({
                                value: value as CVT.MaybeNull<Date>,
                                dictionary,
                                ...datePickerRange,
                              });
                            },
                          }}
                        />
                      </Grid>

                      <Grid size={6} alignSelf="end">
                        <DateField<FormData>
                          name="pet.passingDate"
                          label={dictionary.forms.tributes.passingDate}
                          maxDate={datePickerRange.maxDate}
                          minDate={datePickerRange.minDate}
                          rules={{
                            validate: (value, formValues) => {
                              return validatePassingDate({
                                value: value as CVT.MaybeNull<Date>,
                                birthday: formValues.pet.birthday,
                                dictionary,
                                ...datePickerRange,
                              });
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body1" align="center" color="textSecondary" fontSize={16} fontWeight={400}>
                      {petLifespan}
                    </Typography>
                  )}

                  {tributeStatus === 'published' && tributeUrl && <ShareButtons tributeUrl={tributeUrl} />}

                  <HeartPulse color="primary.main" size={24} mt={2} />

                  <Typography variant="h1" align="center" fontSize={20} fontWeight={600} mt={3}>
                    Journey of Love
                  </Typography>
                </Stack>
              </Grid>
              <Grid size={12} zIndex={1}>
                <Stack mt={2} spacing={3} position="relative" overflow="hidden">
                  {fields.map((field, index) => (
                    <TimelineEventFieldArrayForm
                      key={field.fieldId}
                      index={index}
                      fieldPath={`timeline[${index}]`}
                      onRemove={onRemoveEvent}
                      isEditing={isEditing}
                      isCreating={isCreating}
                    />
                  ))}
                </Stack>
              </Grid>
              {!readonly && (
                <Grid size={12} zIndex={1}>
                  <Stack direction="row" justifyContent="center" alignItems="center" mt={1} mb={1}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={onAddEvent}
                      sx={{
                        bgcolor: 'white',
                        px: 3,
                        py: 1,
                      }}
                      startIcon={<Add />}
                    >
                      Add Memory
                    </Button>
                  </Stack>
                </Grid>
              )}
            </Grid>
          </Stack>
        </Stack>
        {typeof slots?.footer === 'function' ? slots?.footer(formState, methods) : slots?.footer}

      </form>
    </FormProvider>
  );
};

const Background = () => (
  <Box
    sx={{
      position: 'absolute',
      backgroundImage: 'linear-gradient(to right bottom, rgb(254, 247, 246), rgb(255, 241, 242))',
      border: '2px solid #fcdcda',
      width: 'calc(100% - 24px)',
      height: '100%',
      top: 0,
      left: '12px',
      borderRadius: '12px',
    }}
  />
);
